import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Feature, GeoJsonObject, LineString, MultiLineString } from 'geojson';
import { GeoJSON, geoJSON, Layer, Polyline } from 'leaflet';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeoJsonStorageService } from '../../../../shared/geo-json-storage-service/geo-json-storage.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { RolloutProgressReportsService } from '../../../../shared/self-inspection-reports/service/reports/rollout-progress-reports.service';
import { getStringFormatedDateAndTimeWithPipeSymbol } from '../../../../shared/util/date-helper';
import { ExistingSpanPipe } from '../../../../shared/util/existing-span.pipe';
import { PropertySpanPipe } from '../../../../shared/util/property-span.pipe';
import { MAP_RENDERER } from '../../../map-config';
import {
  ConstructionDone,
  SpanProperties,
} from '../../../span-progress-entities';
import { MapApiService } from '../../api/map-api.service';
import { AbstractLayerService } from '../abstract-layer.service';
import { SelectSpanModeService } from './select-span-mode/select-span-mode.service';

@Injectable({ providedIn: 'root' })
export class SpanService extends AbstractLayerService<GeoJSON<SpanProperties> | null> {
  private readonly geoJsonStorageService = inject(GeoJsonStorageService);
  private readonly propertySpanPipe = inject(PropertySpanPipe);
  private readonly existingSpanPipe = inject(ExistingSpanPipe);
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private cancelSub$ = new Subject<void>();

  private readonly _version = signal<string>('');
  readonly version = this._version.asReadonly();

  spanIds$ = new BehaviorSubject<string[]>([]);
  spanData$ = new BehaviorSubject<Feature<LineString, SpanProperties>[]>([]);

  storedV3Reports = this.reportsV3Service.storedV3Reports;

  isSelectSpanMode = this.selectSpanModeService.isSelectSpanMode;

  constructor(
    private readonly apiService: MapApiService,
    private readonly reportsV3Service: RolloutProgressReportsService,
  ) {
    super(null);
  }
  loadData = this.loadSpanData;

  loadSpanData(ifs: string) {
    this.setLoading();
    this.cancelSub$.next();
    this._version.set('');

    this.apiService
      .getSpanGeoJson(ifs)
      .pipe(takeUntil(this.cancelSub$))
      .subscribe({
        next: (geoJsonResponse) => {
          this.geoJsonStorageService.setSpans(ifs, geoJsonResponse);
          this.spanData$.next(geoJsonResponse.features);

          this.apiService.getVersion(ifs).subscribe((latestVersion) => {
            if (latestVersion) {
              this._version.set(
                latestVersion.version +
                  getStringFormatedDateAndTimeWithPipeSymbol(
                    latestVersion.publishedDate,
                  ),
              );
            } else {
              this._version.set('');
            }
          });

          if (!this.loadingState$.getValue().hasLoaded) {
            this.spanIds$.next(
              geoJsonResponse.features.map(
                (feature) => feature.properties.spanId,
              ),
            );

            this.initSpanLayer(geoJsonResponse);
            this.setLoaded();
          }
        },
        error: (err: HttpErrorResponse) => this.handleError(err),
      });
  }

  private initSpanLayer(geoJson: GeoJsonObject) {
    this.layerData = geoJSON<SpanProperties>(geoJson, {
      style: (feature) => {
        return {
          ...this.getSpanColorByProgress(feature?.properties as SpanProperties),
          renderer: MAP_RENDERER,
        };
      },
    }).on('click', (e) => this.onClick(e));
  }

  getSpanColorByProgress(properties: SpanProperties): {
    color: string;
    weight: number;
    dashArray: string | undefined;
    opacity: number;
  } {
    const weightAndDashArrayProperties = {
      weight: 3,
      dashArray: undefined,
    };
    const opacity = this.propertySpanPipe.transform(properties.permissionTypeId)
      ? 0.3
      : 1;

    return {
      ...this.getSpanColorByProgressV3(
        properties,
        weightAndDashArrayProperties,
      ),
      opacity,
    };
  }

  getSpanIds() {
    return this.spanIds$.asObservable();
  }

  getSpanLayersBySpanIds(spanIdArray: string[]): Layer[] | undefined {
    return this.layerData?.getLayers().filter((span: Polyline<LineString>) => {
      const properties = span.feature?.properties as SpanProperties;
      return spanIdArray.includes(properties.spanId);
    });
  }

  getSpanDataValue() {
    return this.spanData$.value;
  }

  getSpanColorByProgressV3 = (
    properties: SpanProperties,
    weightAndDashArrayProperties: { weight: number; dashArray: undefined },
  ): {
    color: string;
    weight: number;
    dashArray: string | undefined;
  } => {
    const isSpanExisting = this.existingSpanPipe.transform(
      properties.accommodationMethodId,
    );
    if (isSpanExisting) {
      return {
        color: RolloutProgressReportEntities.v3.SpanColor.EXISTING,
        ...weightAndDashArrayProperties,
      };
    }

    if (this.storedV3Reports().SPAN?.[properties.spanId]) {
      return {
        color: this.getV3ReportStatusColor(
          this.storedV3Reports().SPAN[properties.spanId].status,
          this.storedV3Reports().SPAN[properties.spanId].isDeviated,
        ),
        ...weightAndDashArrayProperties,
      };
    }

    const isSpanDuktigCompleted = properties.lineStatusId === ConstructionDone;
    return {
      color: this.getV3ReportStatusColor(
        isSpanDuktigCompleted
          ? RolloutProgressReportEntities.v3.ReportStatus.DONE
          : RolloutProgressReportEntities.v3.ReportStatus.PLANNED,
        false,
      ),
      ...weightAndDashArrayProperties,
    };
  };

  getV3ReportStatusColor = (
    status: RolloutProgressReportEntities.v3.ReportStatus,
    isDeviated: boolean,
  ): string => {
    switch (status) {
      case RolloutProgressReportEntities.v3.ReportStatus.PLANNED:
        return RolloutProgressReportEntities.v3.SpanColor.PLANNED;
      case RolloutProgressReportEntities.v3.ReportStatus.IN_PROGRESS:
        return RolloutProgressReportEntities.v3.SpanColor.IN_PROGRESS;
      case RolloutProgressReportEntities.v3.ReportStatus.DONE:
        return RolloutProgressReportEntities.v3.SpanColor.DONE;
      case RolloutProgressReportEntities.v3.ReportStatus.RESTORED:
        return isDeviated
          ? RolloutProgressReportEntities.v3.SpanColor.DEVIATED
          : RolloutProgressReportEntities.v3.SpanColor.RESTORED;
      default:
        return RolloutProgressReportEntities.v3.SpanColor.PLANNED;
    }
  };

  getLinkedSpanIds(
    spanReport: RolloutProgressReportEntities.v3.ReportResponse | undefined,
    spanId: string,
  ): string[] {
    return (spanReport?.spans as string[])?.filter(
      (reportSpanId) => reportSpanId !== spanId,
    );
  }

  getLinkedSpanLayers(
    linkedSpanIds: string[],
  ): Polyline<LineString | MultiLineString, any>[] {
    return this.getSpanLayersBySpanIds(linkedSpanIds) as unknown as Polyline[];
  }

  getLinkedSpanLayersFromReport(
    spanReport: RolloutProgressReportEntities.v3.ReportResponse | undefined,
    spanId: string,
  ): Polyline<LineString | MultiLineString, any>[] {
    return this.getLinkedSpanLayers(this.getLinkedSpanIds(spanReport, spanId));
  }
}
